import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Plus, AlertCircle, FolderPlus } from 'lucide-react';
import { usePrefabStore } from '../../stores/usePrefabStore';
import { useProjectStore } from '../../stores/useProjectStore';
import type { ProjectPrefabConfig } from '../../types/project';
import s from './Pages.module.css';

type WizardStep = 'configure' | 'select-project' | 'done';

export function PrefabDeployPage() {
  const { prefabId } = useParams<{ prefabId: string }>();
  const navigate = useNavigate();
  const catalog = usePrefabStore(p => p.catalog);
  const fetchCatalog = usePrefabStore(p => p.fetchCatalog);
  const configValues = usePrefabStore(p => p.configValues);
  const configErrors = usePrefabStore(p => p.configErrors);
  const setConfigValue = usePrefabStore(p => p.setConfigValue);
  const validateConfig = usePrefabStore(p => p.validateConfig);
  const selectPrefab = usePrefabStore(p => p.selectPrefab);

  const projects = useProjectStore(p => p.projects);
  const loadProjects = useProjectStore(p => p.loadProjects);
  const openProject = useProjectStore(p => p.openProject);
  const createProject = useProjectStore(p => p.createProject);
  const addPrefabToProject = useProjectStore(p => p.addPrefabToProject);

  const prefab = catalog.find(p => p.id === prefabId);

  const [step, setStep] = useState<WizardStep>('configure');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [showNewProject, setShowNewProject] = useState(false);
  const [addedProjectId, setAddedProjectId] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);
  useEffect(() => { loadProjects(); }, [loadProjects]);

  if (prefab && usePrefabStore.getState().selectedPrefabId !== prefab.id) {
    selectPrefab(prefab.id);
  }

  if (!prefab) {
    return (
      <div className={s.page}>
        <div className={s.emptyState}>
          <div className={s.emptyTitle}>Prefab not found</div>
          <button className={s.btnPrimary} onClick={() => navigate('/prefabs')}>Back to Prefabs</button>
        </div>
      </div>
    );
  }

  const handleValidateAndProceed = () => {
    const errors = validateConfig();
    if (Object.keys(errors).length === 0) {
      setStep('select-project');
    }
  };

  const handleAddToProject = async (projectId: string) => {
    setAddError(null);
    try {
      await openProject(projectId);

      const initAction = prefab.actions?.find((a: { name: string }) => a.name === 'init');
      const initArgs: unknown[] = [];
      if (initAction?.parameters) {
        for (const p of initAction.parameters) {
          const raw = configValues[p.name] ?? '';
          const pType = p.type?.type ?? 'string';
          if (pType === 'u64' || pType === 'u128' || pType === 'int') {
            initArgs.push(Number(raw) || 0);
          } else if (pType === 'bool') {
            initArgs.push(raw === 'true' || raw === '1');
          } else {
            initArgs.push(raw);
          }
        }
      }

      const config: ProjectPrefabConfig = {
        prefabId: prefab.id,
        label: prefab.name,
        initArgs,
        configValues: { ...configValues },
        addedAt: Date.now(),
      };

      await addPrefabToProject(config);
      setAddedProjectId(projectId);
      setStep('done');
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to add prefab to project');
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newProjectName.trim()) return;
    setAddError(null);
    try {
      const id = await createProject(newProjectName.trim(), 'prefab');
      await handleAddToProject(id);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to create project');
    }
  };

  const stepIndex = step === 'configure' ? 0 : step === 'select-project' ? 1 : 2;
  const addedProject = projects.find(p => p.id === addedProjectId);

  return (
    <div className={s.page}>
      <button className={s.btnGhost} onClick={() => navigate(`/prefabs/${prefab.id}`)} style={{ marginBottom: 'var(--spacing-lg)' }}>
        <ArrowLeft size={16} /> Back to {prefab.name}
      </button>

      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>Add {prefab.name} to Project</h1>
        <p className={s.pageSubtitle}>Configure this prefab module, then add it to a project. Register all modules together when your project is ready.</p>
      </div>

      {/* Step indicator */}
      <div className={s.steps}>
        {['Configure', 'Select Project', 'Done'].map((label, i) => (
          <div key={label} style={{ display: 'contents' }}>
            <div className={`${s.step} ${i === stepIndex ? s.stepActive : ''} ${i < stepIndex ? s.stepDone : ''}`}>
              <span className={s.stepNumber}>
                {i < stepIndex ? <Check size={14} /> : i + 1}
              </span>
              {label}
            </div>
            {i < 2 && <div className={s.stepConnector} />}
          </div>
        ))}
      </div>

      {/* STEP: Configure */}
      {step === 'configure' && (
        <div style={{ maxWidth: 640 }}>
          {prefab.params.length === 0 && (
            <div style={{ padding: 'var(--spacing-lg)', background: 'rgba(56,139,253,0.08)', border: '1px solid rgba(56,139,253,0.3)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--spacing-xl)', fontSize: 13 }}>
              This prefab requires no configuration — it's ready to add as-is.
            </div>
          )}
          {prefab.params.map(param => (
            <div key={param.key} className={s.formGroup}>
              <label className={s.formLabel}>
                {param.label}
                {param.required && <span style={{ color: 'var(--color-danger)', marginLeft: 4 }}>*</span>}
              </label>
              {param.description && <div className={s.formHint} style={{ marginBottom: 6 }}>{param.description}</div>}

              {(param.uiWidget === 'select' && param.enumValues) ? (
                <select
                  className={s.formSelect}
                  value={configValues[param.key] ?? (param.defaultValue !== undefined ? String(param.defaultValue) : '')}
                  onChange={e => setConfigValue(param.key, e.target.value)}
                >
                  <option value="" disabled>Select {param.label.toLowerCase()}...</option>
                  {param.enumValues.map((val, i) => (
                    <option key={String(val)} value={String(val)}>
                      {param.enumLabels?.[i] ?? String(val)}
                    </option>
                  ))}
                </select>
              ) : param.type === 'boolean' ? (
                <select
                  className={s.formSelect}
                  value={configValues[param.key] ?? 'false'}
                  onChange={e => setConfigValue(param.key, e.target.value)}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : param.uiWidget === 'slider' && param.minimum !== undefined && param.maximum !== undefined ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="range"
                    min={param.minimum}
                    max={param.maximum}
                    step={param.maximum <= 100 ? 1 : Math.max(1, Math.round((param.maximum - param.minimum) / 100))}
                    value={configValues[param.key] ?? String(param.defaultValue ?? param.minimum)}
                    onChange={e => setConfigValue(param.key, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, minWidth: 48, textAlign: 'right' }}>
                    {configValues[param.key] ?? String(param.defaultValue ?? param.minimum)}
                  </span>
                </div>
              ) : param.uiWidget === 'addressPicker' ? (
                <input
                  className={`${s.formInput} ${configErrors[param.key] ? s.formInputError : ''}`}
                  type="text"
                  placeholder="0x..."
                  value={configValues[param.key] ?? ''}
                  onChange={e => setConfigValue(param.key, e.target.value)}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                />
              ) : (
                <input
                  className={`${s.formInput} ${configErrors[param.key] ? s.formInputError : ''}`}
                  type={param.type === 'number' ? 'number' : 'text'}
                  placeholder={param.type === 'number' && param.minimum !== undefined ? `Min: ${param.minimum}` : ''}
                  min={param.minimum}
                  max={param.maximum}
                  value={configValues[param.key] ?? ''}
                  onChange={e => setConfigValue(param.key, e.target.value)}
                />
              )}

              {configErrors[param.key] && (
                <div className={s.formError}>
                  <AlertCircle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
                  {configErrors[param.key]}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 12, marginTop: 'var(--spacing-xl)' }}>
            <button className={s.btnPrimary} onClick={handleValidateAndProceed}>
              Next: Select Project
            </button>
          </div>
        </div>
      )}

      {/* STEP: Select Project */}
      {step === 'select-project' && (
        <div style={{ maxWidth: 640 }}>
          <h3 style={{ fontSize: 15, marginBottom: 'var(--spacing-lg)' }}>Choose a project for {prefab.name}</h3>

          {addError && (
            <div style={{ padding: 'var(--spacing-md)', background: 'rgba(248,81,73,0.1)', border: '1px solid var(--color-danger)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--spacing-lg)', fontSize: 13, color: 'var(--color-danger)' }}>
              <AlertCircle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
              {addError}
            </div>
          )}

          {projects.filter(p => p.registrationStatus !== 'registered').length > 0 && (
            <div style={{ marginBottom: 'var(--spacing-xl)' }}>
              {projects.filter(p => p.registrationStatus !== 'registered').map(project => (
                <div
                  key={project.id}
                  className={`${s.card} ${s.cardClickable}`}
                  style={{
                    marginBottom: 8,
                    cursor: 'pointer',
                    border: selectedProjectId === project.id ? '2px solid var(--color-accent)' : undefined,
                  }}
                  onClick={() => setSelectedProjectId(project.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{project.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                        {project.prefabCount} module{project.prefabCount !== 1 ? 's' : ''} · {project.registrationStatus}
                      </div>
                    </div>
                    {selectedProjectId === project.id && (
                      <button className={s.btnPrimary} onClick={(e) => { e.stopPropagation(); handleAddToProject(project.id); }}>
                        <Plus size={14} /> Add here
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!showNewProject ? (
            <button className={s.btnSecondary} onClick={() => setShowNewProject(true)} style={{ width: '100%', justifyContent: 'center' }}>
              <FolderPlus size={16} /> Create New Project
            </button>
          ) : (
            <div className={`${s.card} ${s.cardStatic}`} style={{ borderColor: 'var(--color-accent)' }}>
              <div className={s.formGroup} style={{ marginBottom: 12 }}>
                <label className={s.formLabel}>Project Name</label>
                <input
                  className={s.formInput}
                  type="text"
                  placeholder="My Game Project"
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={s.btnPrimary} onClick={handleCreateAndAdd} disabled={!newProjectName.trim()}>
                  <Plus size={14} /> Create & Add
                </button>
                <button className={s.btnGhost} onClick={() => setShowNewProject(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 'var(--spacing-xl)' }}>
            <button className={s.btnGhost} onClick={() => setStep('configure')}>
              <ArrowLeft size={14} /> Back to Configure
            </button>
          </div>
        </div>
      )}

      {/* STEP: Done */}
      {step === 'done' && (
        <div style={{ maxWidth: 640 }}>
          <div className={`${s.card} ${s.cardStatic}`} style={{ borderColor: 'var(--color-success)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--spacing-lg)' }}>
              <Check size={20} style={{ color: 'var(--color-success)' }} />
              <h3 style={{ fontSize: 16, color: 'var(--color-success)' }}>Prefab Added to Project</h3>
            </div>
            <table className={s.table}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500, width: '35%' }}>Module</td>
                  <td>{prefab.name}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Project</td>
                  <td>{addedProject?.name ?? '—'}</td>
                </tr>
                {prefab.params.length > 0 && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Config</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {prefab.params.map(p => `${p.key}=${configValues[p.key] || '—'}`).join(', ')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-lg)' }}>
              Configuration is locked. Add more prefabs or register the project when ready.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 'var(--spacing-xl)' }}>
            <button className={s.btnPrimary} onClick={() => navigate(`/projects/${addedProjectId}`)}>
              Go to Project
            </button>
            <button className={s.btnSecondary} onClick={() => navigate('/prefabs')}>
              Add More Prefabs
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
